import {FC, ReactElement, useEffect, useState, useMemo} from 'react';
import {useParams} from 'react-router-dom';
import {ownerPath, videoPath, classPath} from 'routes';
import { Link } from 'react-router-dom';
import useAuthRedirect from 'hooks/useAuthRedirect';
import {Box, Container, Grid, useMediaQuery, useTheme, Fab, Skeleton, IconButton} from '@mui/material';
import ContextError from 'components/ContextError';
import { OwnerHelmet } from 'utils/PromoUtils';
import { useTranslation } from 'react-i18next';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VideoSubject from 'components/VideoSubject';
import VideoSubjectSkeleton from 'components/VideoSubjectSkeleton';
import OwnerHeader from 'components/OwnerHeader';
import { getOwnerMSizeThumb } from 'utils/OwnerUtils';
import {usePlayback} from 'contexts/WatchContext';
import OverflowFilterBar from 'components/OverflowFilterBar';
import { NoSnippet } from 'utils/NoSnippet';
import {useOwnerContextQuery} from 'hooks/apiHooks';
import {VideoSubjectMetadataMessage} from '@/types/VideoSubjectMetadata';

/** Route params for OwnerContext: matches '/o/:musicOwner' **/
/** Route params for OwnerContext: matches '/o/:musicOwner' **/
interface OwnerContextParams {
  /** allow index signature for useParams mapping */
  [key: string]: string | undefined;
  musicOwner?: string;
}

const OwnerContext: FC = (): ReactElement => {
  const theme = useTheme();
  const isXSmall = useMediaQuery(theme.breakpoints.only('xs'));
  // Extract 'musicOwner' from route params
  const { musicOwner = '' } = useParams<OwnerContextParams>();
  // Scroll to top on page mount or owner change
  useEffect(() => { window.scrollTo(0, 0); }, [musicOwner]);

  // Local filter state: class category and language
  const [selectedFilter, setSelectedFilter] = useState<{ titleClass: string; titleLingo: string } | null>(null);
  // 'musicOwner' holds the owner identifier from the route
  // Playback context for queue management
  const { play, isQueueMatch, currentVideo } = usePlayback();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // Fetch owner data, optionally filtered by class and language
  const { data, error, isLoading } = useOwnerContextQuery(
    musicOwner,
    selectedFilter?.titleClass,
    selectedFilter?.titleLingo
  );
  const { t } = useTranslation(['actorAccount', 'titleToolbar']);
  // Promo subject translations for meta description
  const { t: tPromo } = useTranslation('promoSubject');
  // Redirect to login on auth error
  useAuthRedirect(error);
  // Class filter options and video list
  const classBlock = useMemo(
      () => data?.classMetadata?.classMetadata ?? [],
      [data?.classMetadata?.classMetadata]
  );
  const videoBlock: VideoSubjectMetadataMessage[] = data?.videoMetadata?.videoMetadata ?? [];
  // Order-sensitive match against shadow queue
  const queueMatch = isQueueMatch(videoBlock);
  // Disable FAB only when queue matches and a video is currently playing
  const disableFab = queueMatch && Boolean(currentVideo);
  // Loading state
  if (isLoading) {
    // Skeleton layout while loading owner page
    return (
      <Container maxWidth="xl" disableGutters={isXSmall}>
        {/* Header skeleton */}
        <Box sx={{ px: { xs: 1, sm: 2 }, mb: 2 }}>
          <Skeleton variant="rectangular" width="100%" height={theme.spacing(17)} />
        </Box>
        {/* Grid skeleton for videos */}
        <Grid container sx={{ mx: { xs: -1, sm: 0 } }}>
          {Array.from({ length: 12 }).map((_, idx) => (
            <Grid
              key={idx}
              size={{ xs: 6, md: 4, xl: 3 }}
              sx={{ px: { xs: 1, sm: 2 }, py: 1 }}
            >
              <VideoSubjectSkeleton key={idx} />
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }
  // Not-found state
  if (data === null) {
    return <ContextError message={t('notFound')} />;
  }
  // Error state
  if (error) {
    const status = (error as any)?.response?.status;
    const message = status === 404 ? t('notFound') : t('errorLoading');
    return <ContextError message={message} />;
  }

  // navigation now handled via Link props on VideoSubject

  // Page metadata via OwnerHelmet
  const ownerLabel = data?.ownerMetadata?.ownerLabel ?? '';
  const ownerLingo = data?.ownerMetadata?.ownerLingo ?? 'en';
  const actorPromo = tPromo('actorPromo');
  return (
    <>
      <OwnerHelmet
        musicOwner={musicOwner}
        ownerLabel={ownerLabel}
        ownerLingo={ownerLingo}
        actorPromo={actorPromo}
      />
      <Container maxWidth="xl" disableGutters={isXSmall}>
      {/* Owner metadata header */}
      <Box sx={{ px: { sm: 2 }, mb: 2 }}>
        <OwnerHeader
          title={data?.ownerMetadata?.ownerTitle ?? ''}
          avatarUrl={data?.ownerMetadata ? getOwnerMSizeThumb(data.ownerMetadata) : ''}
          lingo={data?.ownerMetadata?.ownerLingo}
        />
      </Box>
      {/* Class filter strip for owner videos */}
      <NoSnippet>
      {classBlock.length > 0 && (
        <OverflowFilterBar
          filters={classBlock}
          selectedFilter={selectedFilter}
          onFilterSelect={setSelectedFilter}
          itemSx={{ cursor: 'filter' }}
          moreSx={{ cursor: 'filter' }}
        />
      )}
      </NoSnippet>
      <Grid container sx={{ mx: { xs: -1, sm: 0 } }}>
        {videoBlock.map((video) => (
          <Grid
            key={video.musicVideo}
            size={{ xs: 6, md: 4, xl: 3 }}
            sx={{ px: { xs: 1, sm: 2 }, py: 1 }}
          >
            <VideoSubject
              video={video}
              videoLink={{
                to: videoPath(video.musicVideo),
                state: { facadeQueue: videoBlock, shadowQueue: videoBlock },
              }}
              ownerLink={{
                to: ownerPath(video.musicOwner)
              }}
            />
          </Grid>
        ))}
      </Grid>
      {/* Explore class catalog outside owner's library */}
      {selectedFilter?.titleLingo && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5, mb: 2.0 }}>
          <IconButton
            component={Link}
            to={classPath(
              selectedFilter.titleLingo,
              selectedFilter.titleClass
            )}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
      )}
      {videoBlock.length > 0 && (
        <Fab
          color="primary"
          disabled={disableFab}
          onClick={() => {
            play(videoBlock[0], videoBlock);
          }}
          sx={{
            position: 'fixed',
            bottom: isMobile ? theme.spacing(9) : theme.spacing(2),
            right: theme.spacing(2),
          }}
        >
          <PlayArrowIcon />
        </Fab>
      )}
    </Container>
  </>
  );
};

export default OwnerContext;
